import { INTERPOLATION_DELAY_MS, type AvatarConfig, type PlayerState, type PositionUpdate } from '@etown/shared';
import { resolveAvatar } from '../lib/avatarParts';
import { SnapshotBuffer } from '../lib/interpolation';
import type { AvatarRenderState } from '../renderer/renderTypes';

interface RemoteEntry {
  buffer: SnapshotBuffer;
  meta: Pick<PlayerState, 'id' | 'displayName' | 'avatarColor' | 'avatar' | 'ringRequired'>;
  lastKnown: { x: number; y: number };
}

/**
 * Remote-Avatare: pro Spieler ein Snapshot-Puffer, gerendert wird
 * INTERPOLATION_DELAY_MS in der Vergangenheit (fluessig bei 15-Hz-Ticks).
 */
export class RemotePlayers {
  private entries = new Map<string, RemoteEntry>();

  upsert(player: PlayerState, nowMs: number): void {
    if (this.entries.has(player.id)) return;
    const buffer = new SnapshotBuffer();
    buffer.push({ t: nowMs, x: player.x, y: player.y, direction: player.direction, yaw: player.yaw });
    this.entries.set(player.id, {
      buffer,
      meta: {
        id: player.id,
        displayName: player.displayName,
        avatarColor: player.avatarColor,
        avatar: player.avatar,
        ringRequired: player.ringRequired,
      },
      lastKnown: { x: player.x, y: player.y },
    });
  }

  remove(id: string): void {
    this.entries.delete(id);
  }

  /** Live-Aenderung der Avatar-Konfiguration (player:avatar). */
  updateAvatar(id: string, avatar: AvatarConfig): void {
    const entry = this.entries.get(id);
    if (entry) entry.meta.avatar = avatar;
  }

  /** Live-Aenderung der Anklingel-Pflicht (player:updated). */
  setRingRequired(id: string, ringRequired: boolean): void {
    const entry = this.entries.get(id);
    if (entry) entry.meta.ringRequired = ringRequired;
  }

  addUpdates(moves: readonly PositionUpdate[], nowMs: number): void {
    for (const move of moves) {
      const entry = this.entries.get(move.id);
      if (!entry) continue;
      entry.buffer.push({ t: nowMs, x: move.x, y: move.y, direction: move.direction, yaw: move.yaw });
      entry.lastKnown = { x: move.x, y: move.y };
    }
  }

  /** Interpolierte Posen fuer den Renderer. */
  sample(nowMs: number, speakingIds: ReadonlySet<string>): AvatarRenderState[] {
    const renderTime = nowMs - INTERPOLATION_DELAY_MS;
    const states: AvatarRenderState[] = [];
    for (const entry of this.entries.values()) {
      const pose = entry.buffer.sample(renderTime);
      if (!pose) continue;
      states.push({
        id: entry.meta.id,
        x: pose.x,
        y: pose.y,
        direction: pose.direction,
        yaw: pose.yaw,
        moving: pose.moving,
        speaking: speakingIds.has(entry.meta.id),
        displayName: entry.meta.displayName,
        avatar: resolveAvatar(entry.meta.avatar, entry.meta.avatarColor),
        dnd: entry.meta.ringRequired,
        isSelf: false,
      });
    }
    return states;
  }

  /** Letzte bekannte (Server-)Positionen — fuer Proximity-UX, Audio und Belegung. */
  positions(): ReadonlyMap<string, { x: number; y: number; displayName: string }> {
    const map = new Map<string, { x: number; y: number; displayName: string }>();
    for (const [id, entry] of this.entries) {
      map.set(id, { ...entry.lastKnown, displayName: entry.meta.displayName });
    }
    return map;
  }

  ids(): string[] {
    return [...this.entries.keys()];
  }
}
